/**
 * Snap guide detection for the CanvasKit editor.
 * Pure functions operating on SceneDocument -- no CanvasKit dependency.
 */
import type { SceneDocument, SceneNode } from '@/lib/scene-document';
import { getNodeAABB, type Rect, type Point } from './canvaskit-hit-test';

// ─── Types ────────────────────────────────────────────────────────────────

export interface SnapGuide {
  orientation: 'vertical' | 'horizontal';
  position: number; // scene-space coordinate
}

export interface SnapResult {
  adjusted: Point;
  guides: SnapGuide[];
}

// ─── Constants ────────────────────────────────────────────────────────────

const SNAP_THRESHOLD = 5; // scene-space pixels
export const GUIDE_COLOR = '#4F46E5';
export const GUIDE_DASH = [4, 4];

// Figma's default layout-grid red, used for column guide overlays.
export const DEFAULT_LAYOUT_GUIDE_COLOR = '#FF0000';
export const DEFAULT_LAYOUT_GUIDE_OPACITY = 0.1;

// ─── Layout guides (column grids) ───────────────────────────────────────────

/** One stretch-columns guide laid across the artboard's content width. */
export interface ColumnGuideConfig {
  count: number;
  gutter: number;
  margin: number;
  color?: string;
  opacity?: number;
  visible?: boolean;
}

/**
 * Per-artboard layout-guide config, stored freeform in the artboard FRAME's
 * `pluginData.layoutGuides` (no schema change — rides through like richText /
 * textWarp). Columns only in v1.
 */
export interface LayoutGuidesConfig {
  columns?: ColumnGuideConfig;
}

/** One resolved column band in scene space. */
export interface ColumnBand {
  x: number;
  width: number;
}

/** Read a node's layout-guide config, if any. */
export function readLayoutGuides(
  node: Pick<SceneNode, 'pluginData'> | undefined | null,
): LayoutGuidesConfig | null {
  const lg = node?.pluginData?.['layoutGuides'];
  return lg && typeof lg === 'object' ? (lg as LayoutGuidesConfig) : null;
}

/**
 * Resolve a columns guide to per-column band rects across an artboard's content
 * width. Stretch model: `margin` on both outer edges, `gutter` between columns,
 * the remaining width split evenly. Returns [] for a degenerate config.
 */
export function computeColumnBands(
  cols: ColumnGuideConfig,
  artboardX: number,
  artboardWidth: number,
): ColumnBand[] {
  const count = Math.max(1, Math.floor(cols.count));
  const margin = Math.max(0, cols.margin);
  const gutter = Math.max(0, cols.gutter);
  const content = artboardWidth - margin * 2;
  if (content <= 0) return [];
  const colWidth = (content - gutter * (count - 1)) / count;
  if (colWidth <= 0) return [];
  const bands: ColumnBand[] = [];
  for (let i = 0; i < count; i++) {
    bands.push({ x: artboardX + margin + i * (colWidth + gutter), width: colWidth });
  }
  return bands;
}

/**
 * Every root artboard/frame carrying a visible columns guide, with its resolved
 * bands. Shared by the chrome overlay (draws them) and the snap collector (adds
 * their edges as targets) so both read one geometry.
 */
export function collectColumnGuides(
  doc: SceneDocument,
): { node: SceneNode; cols: ColumnGuideConfig; bands: ColumnBand[] }[] {
  const out: { node: SceneNode; cols: ColumnGuideConfig; bands: ColumnBand[] }[] = [];
  for (const rootId of doc.rootChildren) {
    const node = doc.nodes[rootId];
    if (!node) continue;
    const cols = readLayoutGuides(node)?.columns;
    if (!cols || cols.visible === false) continue;
    const bands = computeColumnBands(cols, node.x, node.width);
    if (bands.length) out.push({ node, cols, bands });
  }
  return out;
}

// Common padding distances from canvas edges (px).
// Nodes snap to these inset positions for consistent margins.
const CANVAS_PADDING_STEPS = [16, 24, 32, 48];

// ─── Snap target collection ──────────────────────────────────────────────

interface SnapTargets {
  vertical: number[];   // x positions
  horizontal: number[]; // y positions
}

function collectSnapTargets(
  doc: SceneDocument,
  excludeIds: Set<string>,
  includeGuides: boolean,
): SnapTargets {
  const vertical = new Set<number>([0, doc.width / 2, doc.width]);
  const horizontal = new Set<number>([0, doc.height / 2, doc.height]);

  // Add padding-inset snap targets from canvas edges
  for (const pad of CANVAS_PADDING_STEPS) {
    if (pad < doc.width / 2) {
      vertical.add(pad);
      vertical.add(doc.width - pad);
    }
    if (pad < doc.height / 2) {
      horizontal.add(pad);
      horizontal.add(doc.height - pad);
    }
  }

  // Column layout-guide edges: each band's left and right edge is a vertical
  // snap line (same as Figma). Only when guides are shown.
  if (includeGuides) {
    for (const { bands } of collectColumnGuides(doc)) {
      for (const band of bands) {
        vertical.add(Math.round(band.x));
        vertical.add(Math.round(band.x + band.width));
      }
    }
  }

  function collect(nodeIds: string[]) {
    for (const id of nodeIds) {
      if (excludeIds.has(id)) continue;
      const node = doc.nodes[id];
      if (!node || node.visible === false) continue;

      const aabb = getNodeAABB(doc, id);
      vertical.add(Math.round(aabb.x));
      vertical.add(Math.round(aabb.x + aabb.width / 2));
      vertical.add(Math.round(aabb.x + aabb.width));
      horizontal.add(Math.round(aabb.y));
      horizontal.add(Math.round(aabb.y + aabb.height / 2));
      horizontal.add(Math.round(aabb.y + aabb.height));

      // Recurse into groups
      if (node.children?.length) {
        collect(node.children);
      }
    }
  }

  collect(doc.rootChildren);
  return { vertical: [...vertical], horizontal: [...horizontal] };
}

// ─── Snap computation ─────────────────────────────────────────────────────

/**
 * Given a proposed bounding box for the dragged nodes,
 * compute snap-adjusted position and active guide lines.
 */
export function computeSnap(
  doc: SceneDocument,
  draggedIds: Set<string>,
  proposedBounds: Rect,
  opts?: { includeGuides?: boolean },
): SnapResult {
  const targets = collectSnapTargets(doc, draggedIds, opts?.includeGuides ?? true);
  const guides: SnapGuide[] = [];

  let adjustX = 0;
  let adjustY = 0;

  // Edges of the proposed bounds
  const edgesX = [
    proposedBounds.x,
    proposedBounds.x + proposedBounds.width / 2,
    proposedBounds.x + proposedBounds.width,
  ];
  const edgesY = [
    proposedBounds.y,
    proposedBounds.y + proposedBounds.height / 2,
    proposedBounds.y + proposedBounds.height,
  ];

  // Snap X — find the closest matching target
  let bestXDist = SNAP_THRESHOLD + 1;
  for (const target of targets.vertical) {
    for (const edge of edgesX) {
      const dist = Math.abs(edge - target);
      if (dist <= SNAP_THRESHOLD && dist < bestXDist) {
        bestXDist = dist;
        adjustX = target - edge;
      }
    }
  }
  if (bestXDist <= SNAP_THRESHOLD) {
    // Find which target we snapped to (adjusted edge position)
    for (const edge of edgesX) {
      const snappedPos = edge + adjustX;
      if (targets.vertical.includes(Math.round(snappedPos))) {
        guides.push({ orientation: 'vertical', position: Math.round(snappedPos) });
        break;
      }
    }
  }

  // Snap Y — find the closest matching target
  let bestYDist = SNAP_THRESHOLD + 1;
  for (const target of targets.horizontal) {
    for (const edge of edgesY) {
      const dist = Math.abs(edge - target);
      if (dist <= SNAP_THRESHOLD && dist < bestYDist) {
        bestYDist = dist;
        adjustY = target - edge;
      }
    }
  }
  if (bestYDist <= SNAP_THRESHOLD) {
    for (const edge of edgesY) {
      const snappedPos = edge + adjustY;
      if (targets.horizontal.includes(Math.round(snappedPos))) {
        guides.push({ orientation: 'horizontal', position: Math.round(snappedPos) });
        break;
      }
    }
  }

  return {
    adjusted: {
      x: proposedBounds.x + adjustX,
      y: proposedBounds.y + adjustY,
    },
    guides,
  };
}
